// PrefillFunctions.js

import { applySanitiser } from './textSanitisers';
import { normalizeRawAnswer } from '../../../../utils/formDisplayUtils';
import { unwrapNestedValue } from '../../../../utils/formVisibility';

// Normalises Unicode quote characters (smart/curly quotes, prime marks) to their
// standard ASCII equivalents. Mobile keyboards (especially iOS) silently replace
// straight quotes with typographic variants, which breaks regex-based parsing.
const normalizeQuotes = (str) => {
    return str
      .replace(/[\u2018\u2019\u201B\u2032\u02B9]/g, "'")  // single-quote-like → '
      .replace(/[\u201C\u201D\u2033\u02BA]/g, '"');         // double-quote-like → "
  };

// Parses a freeform height string into { feet, inches }.
// Handles a wide variety of patient input styles including apostrophe notation
// (5'11"), double-quote-as-feet (5"11), ft/in labels, slash-separated, decimal,
// and space/dash-separated formats.
//
// IMPORTANT: The order of format checks matters. More specific patterns (like the
// apostrophe and double-quote formats) must come before the general ftInFormat,
// because the general pattern is unanchored and can produce false positives by
// matching a substring (e.g. turning "5"11" into {1, 1} instead of {5, 11}).
const parseHeight = (heightStr) => {
    if (!heightStr) return null;

    // Convert to string, trim whitespace, and normalise any smart/curly quotes
    // to plain ASCII so the regexes below match consistently regardless of the
    // patient's keyboard or device.
    heightStr = normalizeQuotes(String(heightStr).trim());

    // Format 1a: Standard apostrophe notation — 5'6" or 5'6 or 5' 6"
    const apostropheFormat = /^(\d+)'[\s]*(\d+)(?:")?$/;

    // Format 1b: Double-quote used as feet marker — 5"11 or 5" 11
    // Patients sometimes use " (double quote) where they mean ' (feet). In the
    // context of height this is unambiguous: the first number is feet, the second
    // is inches.
    const doubleQuoteFeetFormat = /^(\d+)"[\s]*(\d+)$/;

    // Format 2: Slash separated like 5ft/2in, 5 feet / 2 inches, 5/2, etc.
    const slashFormat = /(\d+)\s*(?:ft|feet|foot)?\s*\/\s*(\d+)\s*(?:in|inch|inches|inchs)?/i;

    // Format 3: 5ft6in, 5ft 6in, 5 ft 6 in, etc. Anchored to avoid partial
    // substring matches that previously caused misparses (e.g. "5"11" matching
    // just "11" and splitting it into {1, 1}).
    const ftInFormat = /^(\d+)\s*(?:ft|feet|foot|')?\s*(\d+)\s*(?:in|inch|inches|inchs|")?$/i;

    // Format 4: Decimal format like 5.5 (feet)
    const decimalFormat = /^(\d+)\.(\d+)$/;

    // Format 5: Simple space or dash separated like "5 6" or "5-6"
    const separatedFormat = /^(\d+)[\s-](\d+)$/;

    let feet = 0;
    let inches = 0;

    // Try standard apostrophe format first (most common correct input)
    let match = heightStr.match(apostropheFormat);
    if (match) {
      feet = parseInt(match[1]);
      inches = parseInt(match[2]);
      return { feet, inches };
    }

    // Try double-quote-as-feet format (common patient mistake: 5"11 meaning 5'11")
    match = heightStr.match(doubleQuoteFeetFormat);
    if (match) {
      feet = parseInt(match[1]);
      inches = parseInt(match[2]);
      return { feet, inches };
    }

    // Try slash format (5ft/2in, 5/2, etc.)
    match = heightStr.match(slashFormat);
    if (match) {
      feet = parseInt(match[1]);
      inches = parseInt(match[2]);
      return { feet, inches };
    }

    // Try ft/in format with various spellings
    match = heightStr.match(ftInFormat);
    if (match) {
      feet = parseInt(match[1]);
      inches = parseInt(match[2]);
      return { feet, inches };
    }

    // Try decimal format (like 5.5 feet)
    match = heightStr.match(decimalFormat);
    if (match) {
      feet = parseInt(match[1]);
      // Convert decimal feet to inches (e.g., 0.5 feet = 6 inches)
      inches = Math.round(parseFloat(`0.${match[2]}`) * 12);
      return { feet, inches };
    }

    // Try space or dash separated format
    match = heightStr.match(separatedFormat);
    if (match) {
      feet = parseInt(match[1]);
      inches = parseInt(match[2]);
      return { feet, inches };
    }

    // If it's just a single number, assume it's feet
    if (/^\d+$/.test(heightStr)) {
      feet = parseInt(heightStr);
      inches = 0;
      return { feet, inches };
    }

    // Could not parse
    return null;
  };

// Extract and format height from multiple possible inputs
const formatHeight = (existingData) => {
  // Try to find height values in the entire form data
  const heightCm = findValueInFormData(existingData, "heightCm");
  const heightFtIn = findValueInFormData(existingData, "heightFtIn");
  
  console.log("heightCm", heightCm);
  console.log("heightFtIn", heightFtIn);
  
  const heightCmExtracted = extractValue(findValueInFormData(existingData, "heightCm"));
  const heightFtInExtracted = extractValue(findValueInFormData(existingData, "heightFtIn"));
  
  console.log("heightCmExtracted", heightCmExtracted);
  console.log("heightFtInExtracted", heightFtInExtracted);
  if (heightCm) {
    return `${heightCm} cm`;
  } else if (heightFtIn) {
    // Parse the height string using our flexible parser
    const parsedHeight = parseHeight(heightFtIn);
    
    if (parsedHeight) {
      const { feet, inches } = parsedHeight;
      
      // Format for display
      return `${feet}'${inches}" (${Math.round(feet * 30.48 + inches * 2.54)} cm)`;
    }
  }
  
  return null;
};

// Extract and format weight from multiple possible inputs
const formatWeight = (existingData) => {
  // Try to find weight values in the entire form data
  const weightKg = findValueInFormData(existingData, "weightKg");
  const weightLb = findValueInFormData(existingData, "weightLb");
  
  if (weightKg) {
    return `${weightKg} kg`;
  } else if (weightLb) {
    // Calculate kg equivalent for display
    const weightInKg = Math.round(parseFloat(weightLb) * 0.453592 * 10) / 10;
    return `${weightLb} lb (${weightInKg} kg)`;
  }
  
  return null;
};

// Special handler for BMI calculation across multiple questions
const calculateBMI = (existingData) => {
  // Extract weight values (preferring kg if available)
  let weight = null;
  let weightUnit = null;
  let originalWeight = null;
  
  // Try to find weight values in the entire form data
  const weightKg = findValueInFormData(existingData, "weightKg");
  const weightLb = findValueInFormData(existingData, "weightLb");
  
  if (weightKg) {
    weight = parseFloat(weightKg);
    weightUnit = "kg";
    originalWeight = weightKg;
  } else if (weightLb) {
    weight = parseFloat(weightLb) * 0.453592; // Convert lb to kg
    weightUnit = "lb";
    originalWeight = weightLb;
  }
  
  // Extract height values (preferring cm if available)
  let height = null;
  let heightUnit = null;
  let originalHeight = null;
  
  // Try to find height values in the entire form data
  const heightCm = findValueInFormData(existingData, "heightCm");
  const heightFtIn = findValueInFormData(existingData, "heightFtIn");
  
  if (heightCm) {
    height = parseFloat(heightCm) / 100; // Convert cm to meters
    heightUnit = "cm";
    originalHeight = heightCm;
  } else if (heightFtIn) {
    // Parse the height string using our flexible parser
    const parsedHeight = parseHeight(heightFtIn);
    
    if (parsedHeight) {
      const { feet, inches } = parsedHeight;
      // Convert to meters: 1 foot = 30.48 cm, 1 inch = 2.54 cm
      height = (feet * 30.48 + inches * 2.54) / 100;
      heightUnit = "ft/in";
      originalHeight = heightFtIn;
    }
  }
  
  // Calculate BMI if we have both values
  if (weight && height) {
    const bmi = weight / (height * height);
    const roundedBmi = Math.round(bmi * 10) / 10; // Round to 1 decimal place
    
    // Get BMI category
    const getBMICategory = (bmi) => {
      const bmiValue = parseFloat(bmi);
      if (bmiValue < 18.5) return "Underweight";
      if (bmiValue >= 18.5 && bmiValue <= 24.9) return "Normal Weight";
      if (bmiValue >= 25 && bmiValue <= 29.9) return "Overweight";
      if (bmiValue >= 30 && bmiValue <= 34.9) return "Obese Class 1";
      if (bmiValue >= 35 && bmiValue <= 39.9) return "Obese Class 2";
      if (bmiValue >= 40) return "Obese Class 3 (Severe Obesity)";
      return "";
    };
    
    const bmiCategory = getBMICategory(roundedBmi);
    
    // Format feet and inches for display if that's what was used
    let heightDisplay = originalHeight;
    if (heightUnit === "ft/in") {
      const parsedHeight = parseHeight(originalHeight);
      if (parsedHeight) {
        heightDisplay = `${parsedHeight.feet}'${parsedHeight.inches}"`;
      }
    }
    
    // Construct a descriptive string showing the calculation
    const sourceDesc = `${originalWeight} ${weightUnit} / ${heightDisplay} ${heightUnit === "cm" ? "cm" : ""}`;
    
    return `${roundedBmi} (${bmiCategory}) - calculated from ${sourceDesc}`;
  }
  
  return null;
};
  
  // Helper function to find a value anywhere in form data
export const findValueInFormData = (existingData, varName) => {
  if (!existingData || !existingData.formData) return null;
  
  // Search through all forms
  for (const formKey in existingData.formData) {
    const form = existingData.formData[formKey];
    
    // Search through all subsections
    for (const subsectionKey in form.formSubsections || {}) {
      const subsection = form.formSubsections[subsectionKey];
      
      // Search through all questions
      for (const questionKey in subsection.questions || {}) {
        const question = subsection.questions[questionKey];
        
        // Search through all subquestions
        for (const subquestionKey in question.subquestions || {}) {
          const subquestion = question.subquestions[subquestionKey];
          
          // Check if this is the variable we're looking for
          if (subquestion.serviceUserVarName === varName && 
              subquestion.answer !== null && 
              subquestion.answer !== undefined &&
              subquestion.answer !== '') {
            // Merge legacy otherAnswer into the modern { value, otherValue }
            // format so extractValue always sees free text regardless of
            // whether the data uses the old or new storage shape.
            return normalizeRawAnswer(subquestion.answer, subquestion.otherAnswer);
          }
        }
      }
    }
  }
  
  return null;
};

// Helper function to extract simple value from answer object or array of objects
const extractValue = (answer) => {
  if (!answer) return null;
  
  if (Array.isArray(answer)) {
    // Return the array with values extracted from each item
    return answer.map(item => {
      if (typeof item === 'object' && item !== null && 'value' in item) {
        return item.otherValue ? `${item.value}: ${item.otherValue}` : item.value;
      }
      return item;
    });
  } else if (typeof answer === 'object' && answer !== null && 'value' in answer) {
    // Extract from simple object
    return answer.otherValue ? `${answer.value}: ${answer.otherValue}` : answer.value;
  }
  
  // Simple value, no extraction needed
  return answer;
};
  
// Helper function to format bullet lists
const formatBulletList = (header, items) => {
  if (!items || items.length === 0) return null;
  return `${header}\n${items.map(item => `• ${item}`).join('\n')}`;
};

// Helper function to format dates in British format with ordinal suffix
export const formatDateWithOrdinal = (date) => {
    if (!date) return "";
    
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleString('en-GB', { month: 'long' });
    const year = d.getFullYear();
    
    // Add ordinal suffix to day
    const ordinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };
    
    return `${day}${ordinalSuffix(day)} ${month} ${year}`;
};

// Helper function to calculate age from date of birth
export const calculateAge = (dob) => {
    if (!dob) return 0;
    
    const birthDate = new Date(dob);
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    
    return age;
};

// English month names in calendar order. formatDateWithOrdinal writes month
// names via toLocaleString('en-GB'), so these are the only names a stored DOB
// prefill string can contain. Used to turn a stored "27th July 1990" back into
// a date without going through Date.parse, which is implementation-defined for
// this format.
const DOB_MONTH_NAMES = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december',
];

// Stored DOB prefill shapes we know how to read:
//   ISO:       "1990-07-27"  (optionally followed by a time, e.g. "T00:00:00Z")
//   Formatted: "27th July 1990 (35 years old)"  — the age suffix is optional so
//              a date written without one still gains a live age.
const DOB_ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/;
const DOB_FORMATTED_PATTERN = /^(\d{1,2})(?:st|nd|rd|th)\s+([A-Za-z]+)\s+(\d{4})(?:\s+\(\d+\s+years?\s+old\))?$/i;

/**
 * Recalculate the age in a date-of-birth prefill string so the Clinical
 * Interview always shows the patient's age as of today.
 *
 * Why this exists: when a patient submits their forms, the DOB is formatted
 * once as "27th July 1990 (35 years old)" and saved to the proforma. If the
 * patient has a birthday between filling in the forms and the appointment,
 * the clinician sees last year's age. Demo patients have the string hard-coded
 * and drift every year. Rather than migrating stored data, every CI display
 * path runs the stored value through this function at render time.
 *
 * Accepts either stored shape (raw ISO date or the formatted string) and
 * returns the canonical formatted string with today's age. Anything else —
 * null, empty, a non-string, or text that is not a recognisable DOB — is
 * returned unchanged, so callers can apply it without a null check and a
 * mis-tagged field can never be mangled.
 *
 * Dates are rebuilt from their parts with the local-time Date constructor.
 * `new Date("1990-07-27")` is parsed as UTC midnight, which in a timezone
 * behind UTC reads back as 26 July; building from parts avoids that shift
 * for both the displayed day and the age comparison.
 *
 * This runs inside render on the Clinical Interview pages. It must never
 * throw: a stored value we cannot read is shown as stored, and the
 * try/catch below is the last line of defence so the proforma always loads.
 *
 * @param {*} prefillString - The stored patientPrefill / guardianPrefill value.
 * @returns {*} "27th July 1990 (36 years old)" or the input unchanged.
 */
export const refreshDobAge = (prefillString) => {
    if (!prefillString || typeof prefillString !== 'string') return prefillString;

    try {
        const trimmed = prefillString.trim();
        let year;
        let monthIndex;
        let day;

        const isoMatch = trimmed.match(DOB_ISO_PATTERN);
        const formattedMatch = isoMatch ? null : trimmed.match(DOB_FORMATTED_PATTERN);

        if (isoMatch) {
            year = Number(isoMatch[1]);
            monthIndex = Number(isoMatch[2]) - 1;
            day = Number(isoMatch[3]);
        } else if (formattedMatch) {
            day = Number(formattedMatch[1]);
            monthIndex = DOB_MONTH_NAMES.indexOf(formattedMatch[2].toLowerCase());
            year = Number(formattedMatch[3]);
        } else {
            return prefillString;
        }

        if (monthIndex < 0 || monthIndex > 11) return prefillString;

        // The local-time constructor rolls invalid days over (31 February
        // becomes 3 March). Checking the parts survived the round trip means
        // an invalid stored date is shown as stored rather than silently moved.
        const birthDate = new Date(year, monthIndex, day);
        if (
            birthDate.getFullYear() !== year
            || birthDate.getMonth() !== monthIndex
            || birthDate.getDate() !== day
        ) {
            return prefillString;
        }

        const age = calculateAge(birthDate);
        return `${formatDateWithOrdinal(birthDate)} (${age} years old)`;
    } catch (error) {
        console.error('refreshDobAge: could not recalculate age, showing stored value', error);
        return prefillString;
    }
};

// Helper function to extract display value from an answer
export const getDisplayValue = (answer, showAnswerOnlyForOtherValue = false) => {
    if (!answer) return "";
    
    if (typeof answer === 'object' && answer.value) {
        // When showAnswerOnlyForOtherValue is true and we have an otherValue,
        // just return the otherValue instead of "value: otherValue"
        if (showAnswerOnlyForOtherValue && answer.otherValue) {
            return answer.otherValue;
        }
        
        return answer.otherValue ? 
            `${answer.value}: ${answer.otherValue}` : answer.value;
    } else if (Array.isArray(answer)) {
        return answer.map(a => {
            if (showAnswerOnlyForOtherValue && a.otherValue) {
                return a.otherValue;
            }
            return a.otherValue ? `${a.value}: ${a.otherValue}` : a.value;
        }).join(', ');
    } else {
        return answer;
    }
};

// Function to handle special formatting for different provider variable names
// Function to handle prefill formatting for provider variables
// Question type handlers - each handles a specific input type
/**
 * Clinic-preferred label shown in provider History when a patient selects the
 * generic "None of the above" option on a multiple-choice question.
 */
export const NONE_OPTION_DISPLAY_LABEL = 'None declared from options provided';

/**
 * Normalise prefill/display strings so the generic none option always reads with
 * the clinic-preferred phrasing. Handles plain values, labelled composites, and
 * legacy demo writes that stored bullet-prefixed variants (e.g. "• None of the above").
 *
 * @param {*} text
 * @returns {*}
 */
export function replaceNoneOfTheAboveDisplay(text) {
  if (text == null || text === '') return text;
  if (typeof text !== 'string') return text;

  return text
    .split('\n')
    .map((line) => {
      if (/^\s*•\s*None of the above\s*$/.test(line)) {
        return NONE_OPTION_DISPLAY_LABEL;
      }
      if (line.trim() === 'None of the above') {
        return NONE_OPTION_DISPLAY_LABEL;
      }
      return line.replace(/None of the above/g, NONE_OPTION_DISPLAY_LABEL);
    })
    .join('\n');
}

export const questionTypeHandlers = {
    // Handler for free text inputs. Applies field-aware sanitisation (e.g. stripping
    // non-digit characters from NHS numbers) when a matching sanitiser exists.
    // The providerVarName is passed through so we can look up the sanitiser; for most
    // freeText fields no sanitiser is defined and the value passes through unchanged.
  freeText: (answer, providerVarName) => {
    if (!answer) return null;
    const { sanitisedValue } = applySanitiser(providerVarName, answer);
    return sanitisedValue;
  },

  freeTextNumerical: (answer) => {
    return answer || null;
  },
  
  // Generic handler for single selections with optional details
  selectOne: (answer) => {
    if (!answer) return null;
    
    if (typeof answer === 'object' && answer.value) {
      // If there are additional details, add them
      return answer.otherValue 
        ? `${answer.value}: ${answer.otherValue}` 
        : answer.value;
    }

    // Modular shape written by the Autism form runner (GenericFormPage) when
    // a selected option has a "Please specify..." box: {selected, otherText}.
    // Without this branch the raw object would be returned as-is and either
    // render as garbage or be dropped, losing the patient's typed detail.
    if (typeof answer === 'object' && answer.selected) {
      return answer.otherText
        ? `${answer.selected}: ${answer.otherText}`
        : answer.selected;
    }

    // Plain string selection (no detail box). Never return a leftover object —
    // a mis-mapped selectMultiple answer ({selectedOptions: [...]}) used to
    // fall through here and get written into patientPrefill as a raw map,
    // which the review UI then showed as "{'selectedOptions': [...]}".
    if (typeof answer === 'string') return answer;

    return null;
  },
  
   // Handler for multi-select questions with optional details
selectMultiple: (answer) => {
  // Normalise the modular shape written by the Autism form runner
  // (GenericFormPage): {selectedOptions: [...], otherTexts: {option: text}}.
  // otherTexts holds what the patient typed into an option's
  // "Please specify..." box (e.g. actual medication names behind
  // "Medications on prescription by a doctor"). We convert it to the
  // array-of-{value, otherValue} shape the mapping below already handles,
  // so the typed detail is preserved as "Option: typed text". Without this,
  // the handler returned null for the modular shape and these answers never
  // reached the clinician proforma or the LLM inputs via client-side prefill.
  if (
    answer &&
    !Array.isArray(answer) &&
    typeof answer === 'object' &&
    Array.isArray(answer.selectedOptions)
  ) {
    const otherTexts = (answer.otherTexts && typeof answer.otherTexts === 'object')
      ? answer.otherTexts
      : {};
    answer = answer.selectedOptions.map(opt => (
      otherTexts[opt]
        ? { value: opt, otherValue: otherTexts[opt] }
        : { value: opt }
    ));
  }

  if (!answer || !Array.isArray(answer) || answer.length === 0) return null;
  
  // Map each option to a bulleted item
  const items = answer.map(option => {
    let value;
    
    if (typeof option === 'object' && option.value) {
      // Unwrap nested {value: {value: ...}} to the leaf string so corrupted
      // data renders the option label instead of "[object Object]".
      const leafValue = unwrapNestedValue(option.value) ?? option.value;
      value = option.otherValue 
        ? `${leafValue}: ${option.otherValue}` 
        : leafValue;
    } else {
      value = option;
    }
    
    // Replace "None of the above" with the preferred phrasing
    if (value === "None of the above") {
      return "None declared from options provided";
    }
    
    return value;
  });
  
  // If there's only one item, no need for bullets
  if (items.length === 1) {
    return items[0];
  }
  
  // Format as bulleted list with line breaks
  return items.map(item => `• ${item}`).join('\n');
},
    
    // Handler for date fields
  date: (answer, providerVarName) => {
    if (!answer) return null;
    
    const formattedDate = formatDateWithOrdinal(answer);
    
    // Add age calculation if this is a date of birth field
    if (providerVarName === 'dateOfBirth') {
      const age = calculateAge(answer);
      return `${formattedDate} (${age} years old)`;
    }
    
    return formattedDate;
  },
    
    // Handler for name fields (special case of concatenation with space separator)
name: (allSubquestions, existingData, formKey, currentPanel, currentQuestionId) => {
    // Object to store all name values
    const nameValues = {};
    
    if (allSubquestions && typeof allSubquestions === 'object') {
      // Get all numeric subquestion indices and sort them
      const subqIndices = Object.keys(allSubquestions)
        .filter(key => !isNaN(parseInt(key)))
        .sort((a, b) => parseInt(a) - parseInt(b));
      
      // Process each subquestion in order
      subqIndices.forEach(sqIdx => {
        const sq = allSubquestions[sqIdx];
        if (sq && sq.serviceUserVarName) {
          const existingValue = existingData?.formData?.[formKey]?.formSubsections?.[currentPanel]
            ?.questions?.[currentQuestionId]?.subquestions?.[sqIdx]?.answer;
          
          if (existingValue) {
            // Special case for title field
            if (sq.serviceUserVarName === 'title') {
              // If it's an object with "Other" selected, just use the otherValue
              if (typeof existingValue === 'object' && 
                  existingValue.value === 'Other' && 
                  existingValue.otherValue) {
                nameValues[sq.serviceUserVarName] = existingValue.otherValue;
              } else {
                // Otherwise use the standard handler
                nameValues[sq.serviceUserVarName] = questionTypeHandlers.selectOne(existingValue);
              }
            } else {
              // For non-title fields, use the standard handler
              nameValues[sq.serviceUserVarName] = questionTypeHandlers.selectOne(existingValue);
            }
          }
        }
      });
      
      // Extract values in the order of subquestion indices
      const orderedValues = subqIndices
        .map(idx => {
          const varName = allSubquestions[idx]?.serviceUserVarName;
          if (!varName || !nameValues[varName] || nameValues[varName].trim() === '') {
            return null;
          }
          
          // Add period after title if it's not already there
          if (varName === 'title') {
            const title = nameValues[varName];
            // Don't add period if title already ends with a period
            return title.endsWith('.') ? title : `${title}.`;
          }
          
          // Apply name capitalisation (first char of each word/hyphenated part)
          // so the prefill shown in Clinical Interview and reports is tidy.
          const { sanitisedValue } = applySanitiser(varName, nameValues[varName]);
          return sanitisedValue;
        })
        .filter(Boolean);
      
      // Join with spaces (special for names)
      return orderedValues.length > 0 ? orderedValues.join(" ") : null;
    }
    
    return null;
  },
  
  // General concatenation handler for addresses, GP details, etc.
concatenateStrings: (answer, providerVarName, allSubquestions, existingData, formKey, currentPanel, currentQuestionId) => {
    // For fields that need concatenation like address, GP info, informants
    const fieldValues = {};
    
    // No need for explicit fieldMapping - we can use all subquestions directly
    if (allSubquestions && typeof allSubquestions === 'object') {
      // Get all numeric subquestion indices and sort them to ensure consistent order
      const subqIndices = Object.keys(allSubquestions)
        .filter(key => !isNaN(parseInt(key)))
        .sort((a, b) => parseInt(a) - parseInt(b));
      
      // Process each subquestion in order, applying field-aware sanitisation
      // (e.g. title-casing address fields, formatting postcodes) before the
      // values are concatenated into the final prefill string.
      subqIndices.forEach(sqIdx => {
        const sq = allSubquestions[sqIdx];
        if (sq && sq.serviceUserVarName) {
          const existingValue = existingData?.formData?.[formKey]?.formSubsections?.[currentPanel]
            ?.questions?.[currentQuestionId]?.subquestions?.[sqIdx]?.answer;
          
          if (existingValue) {
            const rawDisplay = questionTypeHandlers.selectOne(existingValue);
            const { sanitisedValue } = applySanitiser(sq.serviceUserVarName, rawDisplay);
            fieldValues[sq.serviceUserVarName] = sanitisedValue;
          }
        }
      });
      
      // Use the generic concatenation handler with ordered values
      const values = Object.values(fieldValues).filter(Boolean);
      return values.length > 0 ? values.join(", ") : null;
    }
    
    // Fallback to direct value if subquestions aren't available
    return answer || null;
  },

  // Height handler
  height: (answer, providerVarName, allSubquestions, existingData) => {
    return formatHeight(existingData);
  },
  
  // Weight handler
  weight: (answer, providerVarName, allSubquestions, existingData) => {
    return formatWeight(existingData);
  },
  
  // Cross-question calculations
  bmi: (answer, providerVarName, allSubquestions, existingData) => {
    return calculateBMI(existingData);
  },

  // Blood pressure formatter - uses current question subquestions
  bloodPressure: (answer, providerVarName, allSubquestions, existingData, formKey, currentPanel, currentQuestionId) => {
    // Get systolic and diastolic from current question's subquestions
    let systolic = null;
    let diastolic = null;
    
    if (allSubquestions && typeof allSubquestions === 'object') {
      Object.entries(allSubquestions).forEach(([sqIdx, sq]) => {
        if (sq && sq.serviceUserVarName) {
          const existingValue = existingData?.formData?.[formKey]?.formSubsections?.[currentPanel]
            ?.questions?.[currentQuestionId]?.subquestions?.[sqIdx]?.answer;
          
          if (existingValue) {
            if (sq.serviceUserVarName === 'bloodPressureSystolic') {
              systolic = existingValue;
            } else if (sq.serviceUserVarName === 'bloodPressureDiastolic') {
              diastolic = existingValue;
            }
          }
        }
      });
    }
    
    if (systolic && diastolic) {
      return `${systolic}/${diastolic} mmHg`;
    }
    
    return null;
  },
  
  // Pulse formatter - appends bpm
  pulse: (answer) => {
    if (!answer) return null;
    
    // Format pulse with bpm unit
    return `${answer} bpm`;
  },

 // Handler for complications in giving birth - combines data from multiple questions
complicationsInGivingBirth: (answer, providerVarName, allSubquestions, existingData) => {
  const cSection = extractValue(findValueInFormData(existingData, "cSection"));
  const birthOutcomes = extractValue(findValueInFormData(existingData, "complicationsInBirthOutcomes"));
  
  let results = [];
  
  // Process C-section information
  if (cSection) {
    if (cSection === "Yes") {
      results.push("Ever had C-section: Yes");
    } else if (cSection !== "No" && cSection !== "Not applicable" && cSection !== "Prefer not to say") {
      results.push(`Ever had C-section: ${cSection}`);
    }
  }
  
  // Process birth complications information
  if (birthOutcomes) {
    if (birthOutcomes === "Yes") {
      results.push("Birth complications: Yes");
    } else if (birthOutcomes !== "No" && birthOutcomes !== "Not applicable" && birthOutcomes !== "Prefer not to say") {
      results.push(`Birth complications: ${birthOutcomes}`);
    }
  }
  
  return results.length > 0 ? results.join('\n\n') : null;
},

// Handler for pregnancy/breastfeeding status - combines data from multiple questions
currentlyPregnantOrBreastfeeding: (answer, providerVarName, allSubquestions, existingData) => {
  const pregnant = extractValue(findValueInFormData(existingData, "currentlyPregnant"));
  const breastfeeding = extractValue(findValueInFormData(existingData, "currentlyBreastfeeding"));
  
  let results = [];
  
  // Process pregnancy information - always show if question exists
  if (pregnant !== null && pregnant !== undefined) {
    results.push(`Currently pregnant: ${pregnant}`);
  } else {
    // Check if the question exists in the form data structure
    const pregnantExists = findValueInFormData(existingData, "currentlyPregnant") !== null;
    if (pregnantExists) {
      results.push("Currently pregnant: No answer given");
    }
  }
  
  // Process breastfeeding information - always show if question exists
  if (breastfeeding !== null && breastfeeding !== undefined) {
    results.push(`Currently breastfeeding: ${breastfeeding}`);
  } else {
    // Check if the question exists in the form data structure
    const breastfeedingExists = findValueInFormData(existingData, "currentlyBreastfeeding") !== null;
    if (breastfeedingExists) {
      results.push("Currently breastfeeding: No answer given");
    }
  }
  
  return results.length > 0 ? results.join('\n\n') : null;
},

  // Developmental milestones handler
developmentalMilestones: (answer, providerVarName, allSubquestions, existingData) => {
  const lateMilestones = extractValue(findValueInFormData(existingData, "lateDevelopmentalMilestones"));
  const toiletTraining = extractValue(findValueInFormData(existingData, "difficultiesWithToiletTraining"));
  
  let result = [];
  
  // Format late milestones information
  if (lateMilestones) {
    // Replace "None of the above" with the preferred phrasing if needed
    let lateMilestonesText = lateMilestones;
    if (lateMilestonesText === "None of the above") {
      lateMilestonesText = "None declared from options provided";
    }
    
    result.push(`Late developmental milestones (sit, crawl, walk, talk): ${lateMilestonesText}`);
  }
  
  // Format toilet training difficulties
  if (toiletTraining) {
    // Replace "None of the above" with the preferred phrasing if needed
    let toiletTrainingText = toiletTraining;
    if (toiletTrainingText === "None of the above") {
      toiletTrainingText = "None declared from options provided";
    }
    
    result.push(`Difficulties with toilet training: ${toiletTrainingText}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

// Education challenges handler
challengesInEducation: (answer, providerVarName, allSubquestions, existingData) => {
  const isInEducation = extractValue(findValueInFormData(existingData, "currentlyInEducation"));
  const challenges = extractValue(findValueInFormData(existingData, "currentEducationChallenges"));
  
  let result = [];
  
  if (isInEducation) {
    result.push(`Currently in education: ${isInEducation}`);
  }
  
  if (challenges) {
    let challengesText = challenges;
    if (challengesText === "None of the above") {
      challengesText = "None declared from options provided";
    }
    result.push(`Current education challenges: ${challengesText}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

  // Caring responsibilities handler
caringResponsibilities: (answer, providerVarName, allSubquestions, existingData) => {
  const doesCare = extractValue(findValueInFormData(existingData, "doesCareForOthers"));
  const caresFor = findValueInFormData(existingData, "caresForPeople");
  const difficulty = extractValue(findValueInFormData(existingData, "difficultyCaringForOthers"));
  
  let result = [];
  
  if (doesCare) {
    result.push(`Provides care for others: ${doesCare}`);
  }

  if (caresFor && Array.isArray(caresFor) && caresFor.length > 0) {
    const caresForList = extractValue(caresFor).map(value => {
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const formattedCaresList = formatBulletList("Provides care for:", caresForList);
    if (formattedCaresList) result.push(formattedCaresList);
  }

  if (difficulty) {
    let difficultyText = difficulty;
    if (difficultyText === "None of the above") {
      difficultyText = "None declared from options provided";
    }
    result.push(`Difficulty managing caring responsibilities: ${difficultyText}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

// Partner relationship handler
partnerRelationship: (answer, providerVarName, allSubquestions, existingData) => {
  const inRelationship = extractValue(findValueInFormData(existingData, "inPartnerRelationship"));
  const difficulties = extractValue(findValueInFormData(existingData, "partnerRelationshipDifficulties"));
  
  let result = [];
  
  // Format relationship status
  if (inRelationship) {
    result.push(`Is currently in a committed intimate relationship?: ${inRelationship}`);
  }
  
  // Format relationship difficulties
  if (difficulties) {
    // Replace "None of the above" with the preferred phrasing
    let difficultiesText = difficulties;
    if (difficultiesText === "None of the above") {
      difficultiesText = "None declared from options provided";
    }
    
    result.push(`Relationship challenges: ${difficultiesText}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

// Employment history handler
employmentHistory: (answer, providerVarName, allSubquestions, existingData) => {
  const unemploymentPeriod = extractValue(findValueInFormData(existingData, "longestPeriodOfUnemployment"));
  const jobsInFiveYears = extractValue(findValueInFormData(existingData, "jobsInPastFiveYears"));
  const longestJob = extractValue(findValueInFormData(existingData, "longestPeriodInASingleJob"));
  
  let result = [];
  
  // Format longest unemployment period
  if (unemploymentPeriod) {
    result.push(`Longest period of unemployment: ${unemploymentPeriod}`);
  }
  
  // Format number of jobs in past five years
  if (jobsInFiveYears) {
    result.push(`Jobs in past five years: ${jobsInFiveYears}`);
  }
  
  // Format longest time in a single job
  if (longestJob) {
    result.push(`Longest period in a single job: ${longestJob}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

  // Special handler for previousAndCurrentLegalMatters
previousAndCurrentLegalMatters: (answer, providerVarName, allSubquestions, existingData) => {
  // Find both legal history values in form data
  const multipleIssues = extractValue(findValueInFormData(existingData, "legalIssuesMoreThanOnce"));
  const currentIssues = extractValue(findValueInFormData(existingData, "currentLegalIssues"));
  
  let result = [];
  
  // Format multiple legal issues if present
  if (multipleIssues) {
    result.push(`Multiple legal issues: ${multipleIssues}`);
  }
  
  // Format current legal issues if present
  if (currentIssues) {
    result.push(`Current legal issues: ${currentIssues}`);
  }
  
  // Join with double line breaks for visual separation
  return result.length > 0 ? result.join('\n\n') : null;
},

// Physical activity handler
physicalActivity: (answer, providerVarName, allSubquestions, existingData) => {
  const exercise = findValueInFormData(existingData, "doesSignificantExercisePerWeek");
  const strength = findValueInFormData(existingData, "doesStrengthExercises");
  const challenges = findValueInFormData(existingData, "physicalActivityChallenges");
  
  let result = [];
  
  // Format exercise data
  if (exercise) {
    const exerciseText = extractValue(exercise);
    result.push(`Exercise each week: ${exerciseText}`);
  }
  
  // Format strength training data
  if (strength) {
    const strengthText = extractValue(strength);
    result.push(`Strength exercises twice a week: ${strengthText}`);
  }
  
  // Format challenges as a bulleted list
  if (challenges && Array.isArray(challenges) && challenges.length > 0) {
    const challengesList = extractValue(challenges).map(value => {
      // Replace "None of the above" with the preferred phrasing
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const formattedChallenges = formatBulletList("Challenges faced:", challengesList);
    if (formattedChallenges) result.push(formattedChallenges);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

// Diet and nutrition handler
dietAndNutrition: (answer, providerVarName, allSubquestions, existingData) => {
  const diet = findValueInFormData(existingData, "usualDiet");
  const challenges = findValueInFormData(existingData, "dietChallenges");
  
  let result = [];
  
  // Format usual diet as a bulleted list
  if (diet && Array.isArray(diet) && diet.length > 0) {
    const dietList = extractValue(diet).map(value => {
      // Replace "None of the above" with the preferred phrasing
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const formattedDiet = formatBulletList("Usual diet:", dietList);
    if (formattedDiet) result.push(formattedDiet);
  }
  
  // Format challenges as a bulleted list
  if (challenges && Array.isArray(challenges) && challenges.length > 0) {
    const challengesList = extractValue(challenges).map(value => {
      // Replace "None of the above" with the preferred phrasing
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const formattedChallenges = formatBulletList("Diet challenges:", challengesList);
    if (formattedChallenges) result.push(formattedChallenges);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

// Caffeine handler
caffeine: (answer, providerVarName, allSubquestions, existingData) => {
  const drinks = extractValue(findValueInFormData(existingData, "doesDrinkCaffeine"));
  const significant = extractValue(findValueInFormData(existingData, "hasSignificantCaffeinePerDay"));
  const stayAwake = extractValue(findValueInFormData(existingData, "hasCaffeineToStayAwakeOrFocus"));
  
  let result = [];
  
  if (drinks) {
    result.push(`Drinks caffeine: ${drinks}`);
  }

  if (significant) {
    result.push(`Typical daily consumption: ${significant}`);
  }

  if (stayAwake) {
    result.push(`Uses caffeine to help with focus or staying awake: ${stayAwake}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},
  
// Sleep handler
sleep: (answer, providerVarName, allSubquestions, existingData) => {
  const hours = findValueInFormData(existingData, "hoursOfSleepPerNight");
  const rested = findValueInFormData(existingData, "feelsRestedAfterSleep");
  const challenges = findValueInFormData(existingData, "sleepChallenges");
  
  let result = [];
  
  // Format sleep duration
  if (hours) {
    const hoursText = extractValue(hours);
    result.push(`Hours of sleep per night: ${hoursText}`);
  }
  
  // Format feeling rested
  if (rested) {
    const restedText = extractValue(rested);
    result.push(`Feels rested after sleep: ${restedText}`);
  }
  
  // Format challenges as a bulleted list
  if (challenges && Array.isArray(challenges) && challenges.length > 0) {
    const challengesList = extractValue(challenges).map(value => {
      // Replace "None of the above" with the preferred phrasing
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const formattedChallenges = formatBulletList("Sleep challenges:", challengesList);
    if (formattedChallenges) result.push(formattedChallenges);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},
  
// Alcohol handler
alcohol: (answer, providerVarName, allSubquestions, existingData) => {
  const drinks = extractValue(findValueInFormData(existingData, "doesDrinkAlcohol"));
  const significant = extractValue(findValueInFormData(existingData, "hasSignificantAlcoholPerDay"));
  const daily = extractValue(findValueInFormData(existingData, "drinksAlcoholEveryDay"));
  const challenges = findValueInFormData(existingData, "alcoholChallenges");
  const changes = extractValue(findValueInFormData(existingData, "alcoholUseChangesOverTime"));
  const cutDown = extractValue(findValueInFormData(existingData, "alcoholTriedToCutDown"));
  const concerns = extractValue(findValueInFormData(existingData, "alcoholConcernsRaisedByOthers"));
  const help = extractValue(findValueInFormData(existingData, "alcoholSoughtHelp"));

  let result = [];
  
  if (drinks) {
    result.push(`Alcohol consumption: ${drinks}`);
  }

  if (significant) {
    result.push(`More than 14 units per week: ${significant}`);
  }

  if (daily) {
    result.push(`Drinks alcohol every day: ${daily}`);
  }

  if (challenges && Array.isArray(challenges) && challenges.length > 0) {
    const challengesList = extractValue(challenges).map(value => {
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const challengesSection = "Alcohol-related challenges:\n" + 
                             challengesList.map(item => `• ${item}`).join('\n');
    result.push(challengesSection);    
  }

  if (changes) {
    result.push(`Changes in alcohol use: ${changes}`);
  }

  if (cutDown) {
    result.push(`Tried to cut down on alcohol: ${cutDown}`);
  }

  if (concerns) {
    result.push(`Others concerned about drinking: ${concerns}`);
  }

  if (help) {
    result.push(`Sought help for alcohol use: ${help}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},
  
  // Smoking and tobacco handler
smokingAndTobacco: (answer, providerVarName, allSubquestions, existingData) => {
  const smokes = extractValue(findValueInFormData(existingData, "doesSmoke"));
  const tried = extractValue(findValueInFormData(existingData, "hasTriedToQuitSmoking"));
  const focus = extractValue(findValueInFormData(existingData, "smokesForFocus"));
  const quitting = extractValue(findValueInFormData(existingData, "interestedInQuittingSmoking"));
  
  let result = [];
  
  if (smokes) {
    result.push(`Smoker: ${smokes}`);
  }

  if (tried) {
    result.push(`Has tried to quit smoking: ${tried}`);
  }

  if (focus) {
    result.push(`Smokes to help focus or manage concentration: ${focus}`);
  }

  if (quitting) {
    result.push(`Interested in quitting: ${quitting}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

// Drugs and substances handler
drugsAndOtherSubstances: (answer, providerVarName, allSubquestions, existingData) => {
  const used = extractValue(findValueInFormData(existingData, "hasUsedDrugs"));
  const usedFor = findValueInFormData(existingData, "drugsUsedFor");
  const challenges = findValueInFormData(existingData, "drugsChallenges");
  const cutDown = extractValue(findValueInFormData(existingData, "drugsTriedToCutDown"));
  const changes = extractValue(findValueInFormData(existingData, "drugsUseChangesOverTime"));
  const help = extractValue(findValueInFormData(existingData, "drugsSoughtHelp"));
  
  let result = [];
  
  if (used) {
    result.push(`Has used drugs: ${used}`);
  }

  if (usedFor && Array.isArray(usedFor) && usedFor.length > 0) {
    const usedForList = extractValue(usedFor).map(value => {
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const formattedUsedFor = formatBulletList("Uses drugs for:", usedForList);
    if (formattedUsedFor) result.push(formattedUsedFor);
  }

  if (challenges && Array.isArray(challenges) && challenges.length > 0) {
    const challengesList = extractValue(challenges).map(value => {
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const formattedChallenges = formatBulletList("Drug-related challenges:", challengesList);
    if (formattedChallenges) result.push(formattedChallenges);
  }

  if (cutDown) {
    result.push(`Tried to cut down on drugs: ${cutDown}`);
  }

  if (changes) {
    result.push(`Changes in drug use: ${changes}`);
  }

  if (help) {
    result.push(`Sought help for drug use: ${help}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},
  
  // Gambling handler
gambling: (answer, providerVarName, allSubquestions, existingData) => {
  const does = extractValue(findValueInFormData(existingData, "doesBettingActivities"));
  const challenges = findValueInFormData(existingData, "bettingActivitiesChallenges");
  const cutDown = extractValue(findValueInFormData(existingData, "bettingActivitiesTriedToCutDown"));
  const changes = extractValue(findValueInFormData(existingData, "bettingActivitiesChangesOverTime"));
  const help = extractValue(findValueInFormData(existingData, "bettingActivitiesSoughtHelp"));
  
  let result = [];
  
  if (does) {
    result.push(`Participates in betting activities: ${does}`);
  }

  if (challenges && Array.isArray(challenges) && challenges.length > 0) {
    const challengesList = extractValue(challenges).map(value => {
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const formattedChallenges = formatBulletList("Gambling-related challenges:", challengesList);
    if (formattedChallenges) result.push(formattedChallenges);
  }

  if (cutDown) {
    result.push(`Tried to cut down on gambling: ${cutDown}`);
  }

  if (changes) {
    result.push(`Changes in gambling habits: ${changes}`);
  }

  if (help) {
    result.push(`Sought help for gambling: ${help}`);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

// Driving handler
driving: (answer, providerVarName, allSubquestions, existingData) => {
  const licence = extractValue(findValueInFormData(existingData, "currentlyHoldsValidDrivingLicence"));
  const incidents = findValueInFormData(existingData, "drivingIncidents");
  
  let result = [];
  
  // Format licence information
  if (licence) {
    result.push(`Has valid driving licence: ${licence}`);
  }
  
  // Format incidents as a bulleted list
  if (incidents && Array.isArray(incidents) && incidents.length > 0) {
    const incidentsList = extractValue(incidents).map(value => {
      // Replace "None of the above" with the preferred phrasing
      if (value === "None of the above") {
        return "None declared from options provided";
      }
      return value;
    });
    
    const formattedIncidents = formatBulletList("Driving incidents:", incidentsList);
    if (formattedIncidents) result.push(formattedIncidents);
  }
  
  return result.length > 0 ? result.join('\n\n') : null;
},

};
  
  // Map provider variable names to their handler types
  export const providerVarHandlerMap = {
    // Patient Information
    name: 'name', // Special case
    dateOfBirth: 'date', // Special case
    sexAssignedAtBirth: 'selectOne',
    homeAddress: 'concatenateStrings',
    gpSurgery: 'concatenateStrings',    
    genderIdentity: 'selectOne',
    preferredPronouns: 'selectOne',
    sexualOrientation: 'selectOne',
    ethnicity: 'selectOne',
    firstInformant: 'concatenateStrings',
    secondInformant: 'concatenateStrings',
    
    // Presenting Complaint
    referredBy: 'selectOne',
    mainConcernsOrChallenges: 'freeText',
    currentCopingStrategies: 'selectMultiple',
    assessmentGoals: 'selectMultiple',
    involvementOfSocialServices: 'selectOne',

    durationOfDifficulties: 'selectOne',
    recentWorseningOfSymptoms: 'selectOne',

    impactOnWorkOrEducation: 'selectOne',
    impactOnSelfImageAndConfidence: 'selectOne',
    impactOnHealth: 'selectOne',
    impactOnRelationships: 'selectOne',
    impactOnResponsibilities: 'selectOne',

    // Developmental History
    mothersAgeAtBirth: 'selectOne',
    fathersAgeAtBirth: 'selectOne',
    mothersHealthDuringPregnancy: 'selectMultiple',
    mothersSubstancesDuringPregnancy: 'selectMultiple',
    
    detailsOfBirth: 'selectMultiple',

    developmentalMilestones: 'developmentalMilestones', // Special case
    temperamentAsChild: 'selectMultiple',
    contactWithHealthProfessionalsAsChild: 'selectMultiple',

    accidentsLeadingToLossOfConsciousness: 'selectOne',
    accidentsNeedingMedicalTreatment: 'selectOne',
    diagnosesInChildhood: 'selectMultiple',
    friendshipsInChildhood: 'selectOne',

    stabilityAtHome: 'selectOne',
    familyFinancialSituation: 'selectOne',
    careArrangements: 'selectOne',
    relationshipWithParents: 'selectOne',
    conflictWithFamily: 'selectOne',
    supportFromParents: 'selectOne',
    adverseChildhoodExperiences: 'selectMultiple',

    educationExperiences: 'selectMultiple',
    highestLevelOfEducation: 'selectOne',
    challengesInEducation: 'challengesInEducation', // Special case
    
    // Personal and Social History
    livesWith: 'selectMultiple',
    currentLivingArrangement: 'selectMultiple',
    stabilityOfLivingSituation: 'selectOne',
    caringResponsibilities: 'caringResponsibilities', // Special case
    challengesInLivingEnvironment: 'selectMultiple',
    safeHomeEnvironment: 'selectOne',

    partnerRelationship: 'partnerRelationship', // Special case
    familyRelationships: 'selectOne',
    friendships: 'selectOne',
    workplaceRelationships: 'selectOne',
    socialInteractions: 'selectOne',
    supportNetwork: 'selectOne',

    currentWork: 'selectOne',
    employmentHistory: 'employmentHistory', // Special case
    workChallenges: 'selectMultiple',

    // Lifestyle History    
    physicalActivity: 'physicalActivity', // Special case
    dietAndNutrition: 'dietAndNutrition', // Special case
    caffeine: 'caffeine', // Special case
    sleep: 'sleep', // Special case
    selfCare: 'selectMultiple',

    alcohol: 'alcohol', // Special case
    smokingAndTobacco: 'smokingAndTobacco', // Special case
    drugsAndOtherSubstances: 'drugsAndOtherSubstances', // Special case

    internetUse: 'selectMultiple',
    gambling: 'gambling', // Special case
    finances: 'selectMultiple',
    
    driving: 'driving', // Special case

    // Forensic History    
    involvementWithCriminalJusticeSystem: 'selectMultiple',
    previousAndCurrentLegalMatters: 'previousAndCurrentLegalMatters', // Special case
    rehabilitationOrTreatmentProgram: 'selectMultiple',
    historyAsAPerpetrator: 'selectMultiple',
    historyAsAVictim: 'selectMultiple',    

    // Medical History
    bmi: 'bmi', // Special case
    height: 'height', // Special case
    weight: 'weight', // Special case
    pulse: 'pulse', // Special case
    bloodPressure: 'bloodPressure', // Special case

    previousAndCurrentHealthConditions: 'selectMultiple',
    cardiacSymptoms: 'selectMultiple',
    historyOfHeartSurgery: 'selectOne',

    previousHospitalisations: 'selectMultiple',
    aAndEVisits: 'selectMultiple',

    currentMedications: 'selectMultiple',
    
    knownAllergies: 'selectMultiple',
    
    hormonalTransitions: 'selectMultiple',
    currentHrt: 'selectOne',
    unplannedPregnancies: 'selectMultiple',
    teenagePregnancies: 'selectOne',
    complicationsInPregnancy: 'selectOne',
    complicationsInGivingBirth: 'complicationsInGivingBirth', // Special case
    currentlyPregnantOrBreastfeeding: 'currentlyPregnantOrBreastfeeding', // Special case
    
    lastEyeTest: 'selectOne',
    lastHearingTest: 'selectOne',
    assistiveDevices: 'selectOne',

    // Psychiatric History
    moodDisorders: 'selectMultiple',
    anxietyDisorders: 'selectMultiple',
    obsessiveCompulsiveAndRelatedDisorders: 'selectMultiple',
    personalityDisorders: 'selectMultiple',
    neurocognitiveDisorders: 'selectMultiple',
    feedingAndEatingDisorders: 'selectMultiple',
    disruptiveBehaviourDisorders: 'selectMultiple',
    psychoticDisorders: 'selectMultiple',
    substanceRelatedAndAddictiveDisorders: 'selectMultiple',
    
    pastTreatment: 'selectMultiple',
    currentTreatment: 'selectOne',
    
    pastTraumaticExperiences: 'selectOne',
    emotionalSafety: 'selectMultiple',

    // Family History
    familyCardiovascularDiseases: 'selectOne',
    familyNeurologicalDisorders: 'selectOne',
    familyMentalAndBehaviouralDisorders: 'selectMultiple',
    familyNeurodevelopmentalDisorders: 'selectMultiple',
    familySubstanceRelatedAndAddictiveDisorders: 'selectMultiple',
    suddenDeathInFamily: 'selectOne',
    
    // Default handler for unmapped variables
    __default__: 'selectOne'
  };
  
  // Main function export
  export const updateDataModelWithPrefill = (
    providerVarName, 
    data, 
    existingData, 
    currentFormData, 
    formState, 
    currentQuestion, 
    answer, 
    subquestion
  ) => {
    // Reference to current form data
    const formKey = currentFormData.key;
    const currentPanel = formState.currentPanel;
    const currentQuestionId = formState.currentQuestion;
    
    // Get all subquestions from the current question
    const allSubquestions = currentQuestion.subquestions || {};
    
    // Determine the handler type to use
  const handlerType = providerVarHandlerMap[providerVarName] || providerVarHandlerMap.__default__;
  
  let prefillValue = null;
  
  // Handle different calculation types
  if (handlerType === 'name') {
    prefillValue = questionTypeHandlers.name(
      allSubquestions, 
      existingData, 
      formKey, 
      currentPanel, 
      currentQuestionId
    );
  } else if (handlerType === 'concatenateStrings') {
    prefillValue = questionTypeHandlers.concatenateStrings(
      answer,
      providerVarName,
      allSubquestions, 
      existingData, 
      formKey, 
      currentPanel, 
      currentQuestionId
    );
} else if (['height', 'weight', 'bmi', 'previousAndCurrentLegalMatters', 'physicalActivity', 'dietAndNutrition', 
              'caffeine', 'sleep', 'alcohol', 'smokingAndTobacco', 'drugsAndOtherSubstances', 
              'gambling', 'driving', 'caringResponsibilities', 'partnerRelationship', 
              'employmentHistory', 'developmentalMilestones', 'challengesInEducation', 'complicationsInGivingBirth', 'currentlyPregnantOrBreastfeeding'].includes(handlerType)) {
    // For handlers that need to work with multiple fields
    prefillValue = questionTypeHandlers[handlerType](
      answer,
      providerVarName,
      allSubquestions,
      existingData
    );
} else if (handlerType === 'bloodPressure') {
// For blood pressure which needs current question's subquestions
prefillValue = questionTypeHandlers[handlerType](
answer,
providerVarName,
allSubquestions,
existingData,
formKey,
currentPanel,
currentQuestionId
);
  } else {
    // For standard single-question handlers
    prefillValue = questionTypeHandlers[handlerType](
      answer,
      providerVarName
    );
  }
    
    return { prefillValue };
  };